import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useAppUser } from "../../../../../../contexts/user-context/UserContext";
import ExpandableTable from "../../../../../../shared/components/CustomTable/ExpandableTable";
import {
  Country,
  PublicTeam,
} from "../../../../../../shared/constants/Constant";
import { useHistory } from "react-router-dom";
import Pagination from "../../../../../../shared/components/ui/pagination/Pagination";
import PageHeader from "../../../../components/page-header/PageHeader";
import { useAppContext } from "../../../../../../contexts/app-context/AppContext";
import { BarLoader } from "react-spinners";
import Year from "../../../../../../database/models/Year";
import { useYears } from "../../../../../../contexts/year-context/YearContext";
import { DashboardTeamsPages } from "../TeamRoutes";
import {
  TeamData,
  useTeams,
} from "../../../../../../contexts/teams-context/TeamsContext";

enum MCountry {
  SELECT_COUNTRY = "",
  ALL = "ALL",
  MALAYSIA = "MALAYSIA",
  SRI_LANKA = "SRI_LANKA",
  BOSNIA = "BOSNIA",
  JAMAICA = "JAMAICA",
  INDIA = "INDIA",
  INDONESIA = "INDONESIA",
}

/**
 *
 * Profile Information
1. Logo
2. Industry tags
3. Founded
4. Country
5. Social media links
6. Company Name
7. One line introduction
8. Company overview
9. Separate the section & Add profile pic Name, Title, Description
10. Last edited by Admin/Moderator/Member - <Name>
 */

interface Filters {
  teamName: string;
  country: MCountry;
  status: MTeamStatus;
  year: number;
}

enum MTeamStatus {
  STATUS = "",
  ALL = "ALL",
  ACTIVE = "ACTIVE",
  DEACTIVATED = "DEACTIVATED",
}

interface Order {
  current: "TEAM-NAME" | "STATUS" | "COUNTRY";
  teamName: "ASCENDING" | "DESCENDING";
  status: "ASCENDING" | "DESCENDING";
  country: "ASCENDING" | "DESCENDING";
}

interface IState {
  teams: TeamData[];
  loading: boolean;
}
const ITEM_COUNT_IN_PAGE: number = 10;

const Teams = () => {
  const appUser = useAppUser();
  const { push } = useHistory();
  const app = useAppContext();

  const initialState: IState = {
    teams: [],
    loading: false,
  };
  const initialPagination = {
    total_pages: 0,
    page_number: 0,
  };
  const initialFilters: Filters = {
    country: MCountry.SELECT_COUNTRY,
    teamName: "",
    status: MTeamStatus.STATUS,
    year: 2020,
  };

  const initialOrders: Order = {
    current: "TEAM-NAME",
    teamName: "ASCENDING",
    status: "ASCENDING",
    country: "ASCENDING",
  };

  const initialYearState: Year[] = [];

  const [state, setState] = useState(initialState);
  const { teams } = useTeams();
  const [filters, setFilters] = useState(initialFilters);
  const [orders, setOrders] = useState(initialOrders);
  const [pagination, setPagination] = useState(initialPagination);

  const yearsObject = useYears();
  const [years, setYears] = useState(initialYearState);

  useEffect(() => {
    setYears(() => {
      const yearsArr: Year[] = Object.assign([], [yearsObject][0]);
      const sorted: Year[] = yearsArr
        ? yearsArr.sort((a, b) => (a.appliedYear > b.appliedYear ? 1 : -1))
        : [];

      if (sorted.length > 0) {
        setFilters((prevState) => {
          prevState.year = sorted[sorted.length - 1].appliedYear;

          return prevState;
        });
      }

      return sorted;
    });
  }, [yearsObject]);

  useEffect(() => {
    if (state.teams.length % ITEM_COUNT_IN_PAGE === 0) {
      const totalPages = state.teams.length / ITEM_COUNT_IN_PAGE;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    } else {
      const totalPages =
        Math.floor(state.teams.length / ITEM_COUNT_IN_PAGE) + 1;
      setPagination((ps) => ({ ...ps, total_pages: totalPages }));
    }
  }, [state.teams]);

  const paginate = (
    array: TeamData[],
    page_number: number,
    page_size: number
  ): TeamData[] =>
    array.slice(page_number * page_size, (page_number + 1) * page_size);

  useEffect(() => {
    setState((ps) => ({ ...ps, teams }));
  }, [teams, appUser.userRole.isAdmin]);

  useEffect(() => {
    let c_teams = teams;

    c_teams = teams.filter((t) => t.name !== PublicTeam.PUBLIC_TEAM);

    if (filters.teamName.trim().length > 0) {
      c_teams = c_teams.filter((team) =>
        team.name?.toLocaleLowerCase().includes(filters.teamName.toLowerCase())
      );
    }
    if (filters.country !== MCountry.ALL) {
      if (filters.country !== MCountry.SELECT_COUNTRY) {
        c_teams = c_teams.filter(
          (team) => team.country.toString() === filters.country.toString()
        );
      }
    }
    if (filters.year > 0 && years && years.length > 0) {
      const startsAt: Date = years.filter(
        (y) => y.appliedYear === filters.year
      )[0]?.startsAt;
      const endsAt: Date = years.filter(
        (y) => y.appliedYear === filters.year
      )[0]?.endsAt;

      c_teams = c_teams.filter((teamData) =>
        teamData.createdAt
          ? teamData.createdAt >= startsAt && teamData.createdAt <= endsAt
          : true
      );
    }

    switch (orders.current) {
      case "STATUS":
        if (orders.status === "ASCENDING") {
          c_teams.sort((a: TeamData, b: TeamData) =>
            a.active === b.active ? 0 : a.active ? -1 : 1
          );
        } else {
          c_teams.sort((a: TeamData, b: TeamData) =>
            a.active === b.active ? 0 : a.active ? 1 : -1
          );
        }
        break;
      case "COUNTRY":
        if (orders.country === "ASCENDING") {
          c_teams.sort((a: TeamData, b: TeamData) => {
            if (a.country && b.country) {
              return b.country?.toLowerCase() > a.country?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        } else {
          c_teams.sort((a: TeamData, b: TeamData) => {
            if (a.country && b.country) {
              return b.country?.toLowerCase() < a.country?.toLowerCase()
                ? -1
                : 1;
            } else {
              return 0;
            }
          });
        }
        break;
      default:
        if (orders.teamName === "ASCENDING") {
          c_teams.sort((a: TeamData, b: TeamData) => {
            if (a.name && b.name) {
              return b.name?.toLowerCase() > a.name?.toLowerCase() ? -1 : 1;
            } else {
              return 0;
            }
          });
        } else {
          c_teams.sort((a: TeamData, b: TeamData) => {
            if (a.name && b.name) {
              return b.name?.toLowerCase() < a.name?.toLowerCase() ? -1 : 1;
            } else {
              return 0;
            }
          });
        }
        break;
    }
    setState((ps) => ({ ...ps, teams: c_teams }));
  }, [filters.country, filters.teamName, filters.year, years, teams, orders]);

  useEffect(() => {
    setPagination((ps) => {
      return { ...ps, page_number: 0 };
    });
  }, [filters.country, filters.teamName]);

  const getCountryString = (c: Country) => {
    switch (c) {
      case Country.BOSNIA:
        return "Bosnia and Herzegovina";
      case Country.JAMAICA:
        return "Jamaica";
      case Country.MALAYSIA:
        return "Malaysia";
      case Country.INDONESIA:
        return "Indonesia";
      case Country.INDIA:
        return "India";
      default:
        return "Sri Lanka";
    }
  };

  return (
    <div>
      <PageHeader sidebarOnly={true} title="Teams" />
      <Container
        fluid
        style={{
          paddingTop: "210px",
          paddingLeft: "65px",
          paddingRight: "50px",
          paddingBottom: "10px",
        }}
      >
        <Row>
          <Col
            xs={12}
            md={3}
            xl={3}
            className="d-flex align-items-center ml-md-0 my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <input
                className="app-input input-small search-img"
                style={{ paddingRight: "30px" }}
                placeholder="Team Name"
                value={filters.teamName}
                onChange={(e) => {
                  setFilters({ ...filters, teamName: e.target.value });
                }}
              />
            </div>
          </Col>
          {appUser.userRole.isAdmin && (
            <Col
              xs={12}
              md={3}
              xl={3}
              className="d-flex align-items-center my-1 my-md-0"
              style={{ minWidth: "250px" }}
            >
              <div style={{ flex: "1" }}>
                <select
                  className="app-input input-small filter-img"
                  value={filters.country}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      country: e.target.value as any,
                    });
                  }}
                >
                  <option value={MCountry.SELECT_COUNTRY} disabled hidden>
                    Country
                  </option>
                  <option value={MCountry.ALL}>All Countries</option>
                  <option value={MCountry.MALAYSIA}>Malaysia</option>
                  <option value={MCountry.SRI_LANKA}>Sri Lanka</option>
                  <option value={MCountry.BOSNIA}>
                    Bosnia and Herzegovina
                  </option>
                  <option value={MCountry.JAMAICA}>Jamaica</option>
                  <option value={MCountry.INDONESIA}>Indonesia</option>
                  <option value={MCountry.INDIA}>India</option>
                </select>
              </div>
            </Col>
          )}
          {years && years.length > 0 ? (
            <Col
              xs={12}
              md={2}
              xl={2}
              className="d-flex align-items-center my-1 my-md-0"
              style={{ minWidth: "250px" }}
            >
              <div style={{ flex: "1" }}>
                {" "}
                <select
                  className="app-input input-small filter-img"
                  value={filters.year}
                  onChange={(e) => {
                    setFilters({
                      ...filters,
                      year: parseInt(e.target.value),
                    });
                  }}
                >
                  <option key={0} value={""} disabled hidden>
                    Year
                  </option>
                  {years
                    .sort((a, b) => (a.appliedYear > b.appliedYear ? -1 : 1))
                    .map((y, i) => (
                      <option key={i + 1} value={y.appliedYear}>
                        {y.appliedYear}
                      </option>
                    ))}
                </select>
              </div>
            </Col>
          ) : (
            <></>
          )}
          <Col
            xs={12}
            md={2}
            xl={2}
            className="d-flex align-items-center my-1 my-md-0"
          >
            <div style={{ flex: "1" }}>
              <button
                className="app-button"
                style={{
                  backgroundColor: "#6c757d",
                  padding: "3px 10px 3px 10px",
                }}
                onClick={() => setFilters(initialFilters)}
              >
                Clear filters
              </button>
            </div>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col xs={12}>
            <BarLoader
              height={4}
              width={"100%"}
              color={"#006699"}
              loading={state.loading}
            />
          </Col>
        </Row>
        <Row className="mt-2 scroll" style={{ overflowX: "auto" }}>
          <Col xs={12} style={{ minHeight: "520px" }}>
            <ExpandableTable
              onStatusUpdate={(isLoading: boolean) => {
                setState({ ...state, loading: isLoading });
              }}
              orders={orders}
              onOrderUpdate={(ordersNew: Order) => {
                setOrders(ordersNew);
              }}
              teams={paginate(
                state.teams,
                pagination.page_number,
                ITEM_COUNT_IN_PAGE
              ).map((team) => {
                return {
                  id: team.id || "",
                  members: team.members.map((member) => {
                    return {
                      id: member.id || "",
                      fullName: member.fullName || "",
                      email: member.email,
                    };
                  }),
                  name: team.name || "",
                  status: team.active ? "Active" : "Deactivated",
                  country: getCountryString(team.country),
                  onClick: (id) => {
                    app.pushBackURL(DashboardTeamsPages.MEMBERS_TEAMS);
                    push(
                      DashboardTeamsPages.TEAM_PROFILE_TEAM.replace(":id", id)
                    );
                  },
                  onEdit: (id) => {
                    push(
                      DashboardTeamsPages.TEAM_PROFILE_EDIT.replace(":id", id)
                    );
                  },
                };
              })}
            />
          </Col>
          <Col xs={12}>
            <Pagination
              pInfo={{
                page: pagination.page_number,
                totalPages: pagination.total_pages,
              }}
              onPageChanged={(page) => {
                setPagination({ ...pagination, page_number: page });
              }}
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Teams;
